<script setup>
// NOTICE: KAPTION-INTEGRATION
// security-center.TWO_STEP.4 — Verificación en dos pasos (canvas: Main ›
// Verificación en dos pasos): the ACCOUNT policy from two-step-verification
// (#509) — who must use it, grace days, allowed methods, accepting the IdP's
// MFA — and who is still missing a method, with #509's reset action. Personal
// methods stay in Profile. Only reachable where the two-step feature is on.
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import Spinner from 'shared/components/Spinner.vue';
import KaptionSecurityAPI from 'dashboard/api/kaptionSecurity';
import { useKaptionSecurity } from 'dashboard/composables/useKaptionSecurity';
import KaptionTwoStepResetButton from 'dashboard/components/kaption/KaptionTwoStep/KaptionTwoStepResetButton.vue';
import KaptionSecuritySection from '../components/KaptionSecuritySection.vue';
import { useKaptionSecurityText } from '../useKaptionSecurityText';

const { t } = useI18n();
const router = useRouter();
const accountId = useMapGetter('getCurrentAccountId');
const { overview, twoStepAvailable, fetchOverview } = useKaptionSecurity();
const { provider } = useKaptionSecurityText(overview);

const METHODS = ['passkey', 'whatsapp', 'authenticator'];
const GRACE_OPTIONS = [14, 7, 3, 0];

const detail = ref(null);
const busy = ref(false);

const load = async () => {
  try {
    const { data } = await KaptionSecurityAPI.twoStep();
    detail.value = data;
  } catch (e) {
    router.replace({
      name: 'settings_kaption_security',
      params: { accountId: accountId.value },
    });
  }
};

onMounted(() => {
  if (!twoStepAvailable.value) {
    router.replace({
      name: 'settings_kaption_security',
      params: { accountId: accountId.value },
    });
    return;
  }
  load();
});

const save = async changes => {
  busy.value = true;
  try {
    const { data } = await KaptionSecurityAPI.updateTwoStep(changes);
    detail.value = data;
    fetchOverview({ force: true });
    useAlert(t('KAPTION_SECURITY.COMMON.SAVED'));
  } catch (e) {
    useAlert(
      e?.response?.data?.error_code === 'no_methods'
        ? t('KAPTION_SECURITY.TWO_STEP.NO_METHODS')
        : t('KAPTION_SECURITY.COMMON.ERROR')
    );
  } finally {
    busy.value = false;
  }
};

const requiredFor = computed({
  get: () => detail.value?.required_for || 'admins',
  set: value => save({ required_for: value }),
});
const grace = computed({
  get: () => detail.value?.grace_days ?? 14,
  set: value => save({ grace_days: Number(value) }),
});
const graceOptions = computed(() => {
  const current = detail.value?.grace_days;
  return GRACE_OPTIONS.includes(current) || current == null
    ? GRACE_OPTIONS
    : [current, ...GRACE_OPTIONS];
});

const allowed = computed(() => detail.value?.methods || METHODS);
const toggleMethod = method => {
  const next = allowed.value.includes(method)
    ? allowed.value.filter(m => m !== method)
    : [...allowed.value, method];
  if (!next.length) {
    useAlert(t('KAPTION_SECURITY.TWO_STEP.NO_METHODS'));
    return;
  }
  save({ methods: next });
};

const ssoConnected = computed(() => !!detail.value?.sso_connected);
const acceptIdp = computed(() => !!detail.value?.accept_idp_mfa);

const missing = computed(() => detail.value?.missing || []);

const missingNote = person => {
  if (person.blocked)
    return {
      tone: 'text-n-ruby-11',
      text: t('KAPTION_SECURITY.TWO_STEP.GRACE_ENDED'),
    };
  if (person.grace_ends_at) {
    const days = Math.max(
      0,
      Math.ceil(
        (new Date(person.grace_ends_at).getTime() - Date.now()) / 86400000
      )
    );
    return {
      tone: 'text-n-amber-11',
      text: t('KAPTION_SECURITY.TWO_STEP.GRACE_LEFT', { days }),
    };
  }
  return {
    tone: 'text-n-slate-11',
    text: t('KAPTION_SECURITY.TWO_STEP.GRACE_NOT_STARTED'),
  };
};

const methodsText = person =>
  person.methods?.length
    ? t('KAPTION_SECURITY.TWO_STEP.HAS_OTHER', {
        methods: person.methods
          .map(m => t(`KAPTION_SECURITY.TWO_STEP.METHOD.${m}`))
          .join(', '),
      })
    : t('KAPTION_SECURITY.TWO_STEP.NO_METHOD');
</script>

<template>
  <div v-if="!detail" class="flex justify-center py-16"><Spinner /></div>
  <div v-else class="flex flex-col gap-11">
    <KaptionSecuritySection
      :title="t('KAPTION_SECURITY.TWO_STEP.TITLE')"
      :description="t('KAPTION_SECURITY.TWO_STEP.DESC')"
    >
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div class="flex flex-col gap-1.5">
          <label for="ks-2fa-who" class="text-sm font-medium text-n-slate-12">{{
            t('KAPTION_SECURITY.TWO_STEP.REQUIRED_FOR')
          }}</label>
          <select
            id="ks-2fa-who"
            v-model="requiredFor"
            :disabled="busy"
            class="h-10 px-3 text-sm border rounded-xl border-n-weak bg-n-alpha-black2 text-n-slate-12"
          >
            <option value="admins">
              {{ t('KAPTION_SECURITY.TWO_STEP.ADMINS') }}
            </option>
            <option value="everyone">
              {{ t('KAPTION_SECURITY.TWO_STEP.EVERYONE') }}
            </option>
          </select>
        </div>
        <div class="flex flex-col gap-1.5">
          <label
            for="ks-2fa-days"
            class="text-sm font-medium text-n-slate-12"
            >{{ t('KAPTION_SECURITY.TWO_STEP.GRACE') }}</label
          >
          <select
            id="ks-2fa-days"
            v-model="grace"
            :disabled="busy"
            class="h-10 px-3 text-sm border rounded-xl border-n-weak bg-n-alpha-black2 text-n-slate-12"
          >
            <option v-for="days in graceOptions" :key="days" :value="days">
              {{
                days === 0
                  ? t('KAPTION_SECURITY.TWO_STEP.GRACE_NOW')
                  : t('KAPTION_SECURITY.TWO_STEP.GRACE_DAYS', { count: days })
              }}
            </option>
          </select>
        </div>
      </div>

      <fieldset class="flex flex-col gap-2 p-0 m-0 border-0">
        <legend class="mb-2 text-sm font-medium text-n-slate-12">
          {{ t('KAPTION_SECURITY.TWO_STEP.METHODS') }}
        </legend>
        <label
          v-for="method in METHODS"
          :key="method"
          class="flex items-center gap-3 text-sm cursor-pointer text-n-slate-12"
        >
          <input
            type="checkbox"
            class="accent-n-brand"
            :checked="allowed.includes(method)"
            :disabled="busy"
            @change="toggleMethod(method)"
          />
          {{ t(`KAPTION_SECURITY.TWO_STEP.METHOD.${method}`) }}
        </label>
        <span class="text-[13px] text-n-slate-11">{{
          t('KAPTION_SECURITY.TWO_STEP.METHODS_HELP')
        }}</span>
      </fieldset>

      <label
        class="flex gap-3 text-sm"
        :class="
          ssoConnected ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'
        "
        data-testid="ks-accept-idp-mfa"
      >
        <input
          type="checkbox"
          class="mt-0.5 accent-n-brand"
          :checked="acceptIdp"
          :disabled="!ssoConnected || busy"
          @change="save({ accept_idp_mfa: !acceptIdp })"
        />
        <span class="flex flex-col gap-1">
          <span class="font-semibold text-n-slate-12">{{
            t('KAPTION_SECURITY.TWO_STEP.IDP_TITLE', { provider })
          }}</span>
          <span class="text-n-slate-11">{{
            ssoConnected
              ? t('KAPTION_SECURITY.TWO_STEP.IDP_DESC', { provider })
              : t('KAPTION_SECURITY.TWO_STEP.IDP_NEEDS_SSO')
          }}</span>
        </span>
      </label>

      <p class="m-0 text-[13px] text-n-slate-11">
        {{
          t('KAPTION_SECURITY.TWO_STEP.PROGRESS', {
            with: detail.with_method,
            total: detail.required_total,
          })
        }}
      </p>
    </KaptionSecuritySection>

    <KaptionSecuritySection
      :title="t('KAPTION_SECURITY.TWO_STEP.MISSING_TITLE')"
    >
      <p v-if="!missing.length" class="m-0 text-sm text-n-slate-11">
        {{ t('KAPTION_SECURITY.TWO_STEP.MISSING_EMPTY') }}
      </p>
      <div v-else class="border rounded-xl border-n-weak">
        <div
          v-for="(person, index) in missing"
          :key="person.id"
          class="flex flex-wrap items-center gap-3 px-4 py-3"
          :class="{ 'border-t border-n-weak': index > 0 }"
        >
          <div class="flex flex-col flex-1 min-w-0 gap-0.5">
            <span class="text-sm font-semibold truncate text-n-slate-12">{{
              person.name || person.email
            }}</span>
            <span class="text-[13px] truncate text-n-slate-11">
              {{ person.email }} ·
              {{ t(`KAPTION_SECURITY.TWO_STEP.ROLE_${person.role}`) }} ·
              {{ methodsText(person) }}
            </span>
          </div>
          <span class="text-[13px]" :class="missingNote(person).tone">{{
            missingNote(person).text
          }}</span>
          <KaptionTwoStepResetButton
            v-if="person.methods?.length"
            :user-id="person.id"
            :name="person.name"
          />
        </div>
      </div>
    </KaptionSecuritySection>
  </div>
</template>
